import { useParams } from "react-router";
import { useEffect, useState, useMemo } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";
import DoctorProfileCard from "./components/DoctorProfileCard";
import DaySelector from "./components/DaySelector";
import AppointmentSlots from "./components/AppointmentSlots";

interface Treatment {
  name: string;
}

interface Doctor {
  doctorId: number;
  name: string;
  specialisation: string;
  profile: string;
  phone: string;
  treatments: Treatment[];
  availableDays: { day: string; available: boolean }[];
  appointments: any[];
}

export default function BookDoctor() {
  const { id } = useParams();
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchDoctor();
  }, [id]);

  useEffect(() => {
    fetchPatientAppointments();
  }, []);

  const fetchDoctor = () => {
    setLoading(true);
    axiosInstance.get("/appointment/doctors-list")
      .then((res) => {
        const found = res.data.find((d: any) => d.doctorId === Number(id));
        setDoctor(found || null);
      })
      .catch((err) => {
        console.error("Error fetching doctor:", err);
        toast.error("Failed to load doctor details");
      })
      .finally(() => setLoading(false));
  };

  const fetchPatientAppointments = () => {
    axiosInstance.get("/appointment/patient")
      .then((res) => {
        setAppointments(res.data);
      })
      .catch((err) => {
        console.error("Error fetching patient appointments:", err);
      });
  };

  const handleBook = async (date: string, start: string, end: string) => {
    if (!doctor) return;

    try {
      const payload = {
        doctorPhone: doctor.phone,
        date,
        start,
        end,
      };

      await axiosInstance.post("/appointment/book", payload);
      toast.success(`Appointment booked for ${date} • ${start}-${end}`);

      // Refresh doctor data to update slots and patient appointments to show "Booked"
      fetchDoctor();
      fetchPatientAppointments();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to book appointment");
    }
  };

  const activeDays = useMemo(() => {
    return doctor?.availableDays.filter((d) => d.available) || [];
  }, [doctor]);

  useEffect(() => {
    if (activeDays.length > 0 && !selectedDay) {
      setSelectedDay(activeDays[0].day);
    }
  }, [activeDays, selectedDay]);

  const filteredAppointments = useMemo(() => {
    if (!doctor) return [];

    // Filter appointments from TODAY onward
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let filtered = doctor.appointments.filter((appt: any) => {
      const apptDate = new Date(appt.date);
      apptDate.setHours(0, 0, 0, 0);
      return apptDate >= today;
    });

    // If a day tag is selected → filter by weekday
    if (selectedDay) {
      filtered = filtered.filter((appt: any) => {
        const weekday = new Date(appt.date)
          .toLocaleDateString("en-US", { weekday: "long" })
          .toUpperCase();
        return weekday === selectedDay;
      });
    }

    return filtered;
  }, [doctor, selectedDay]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-xl text-gray-600">Doctor not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <DoctorProfileCard doctor={doctor} />

      <DaySelector
        activeDays={activeDays}
        selectedDay={selectedDay}
        onSelectDay={setSelectedDay}
      />

      <AppointmentSlots
        appointments={filteredAppointments}
        userAppointments={appointments}
        onBook={handleBook}
      />
    </div>
  );
}
